import { Button, Dialog, DialogBody, DialogContent, EmphasizedText } from '@civilgrid/ui/components';
import { useLocation } from '@tanstack/react-router';
import { useRef } from 'react';
import { useSnapshot } from 'valtio';
import {
  PRIVACY_NOTICE_DOCUMENT,
  TERMS_OF_USE_DOCUMENT,
  WELCOME_DISCLAIMER_ACCEPT_LABEL,
  WELCOME_DISCLAIMER_BULLETS,
  WELCOME_DISCLAIMER_INTRO,
  WELCOME_DISCLAIMER_LEAD_IN,
  WELCOME_DISCLAIMER_TITLE,
} from '~/constants/disclaimer';
import rootStore, { DISCLAIMER_VIEWED_KEY } from '../../store';
import { ConfidentialityNotice } from './ConfidentialityNotice';
import { isDisclaimerEligible, useDisclaimerBlocking } from './useDisclaimerBlocking';

function DocumentLink({ document }: { document: typeof TERMS_OF_USE_DOCUMENT | typeof PRIVACY_NOTICE_DOCUMENT }) {
  return (
    <a
      href={document.href}
      target="_blank"
      rel="noopener noreferrer"
      download={document.fileName}
      className="text-blue-50 underline hover:text-blue-60"
      data-testid={`disclaimer-link-${document.fileName}`}
    >
      {document.label}
    </a>
  );
}

export default function Disclaimer() {
  const location = useLocation();
  const { me } = useSnapshot(rootStore);
  // Eligibility decides whether this surface mounts; blocking decides whether it is open, so Radix
  // still animates the close on accept.
  const isBlocking = useDisclaimerBlocking();
  const acceptButtonRef = useRef<HTMLButtonElement>(null);

  if (isDisclaimerEligible({ isOnboarded: me?.isOnboarded, pathname: location.pathname })) {
    return (
      <Dialog open={isBlocking}>
        <DialogContent
          title={WELCOME_DISCLAIMER_TITLE}
          dismissible={false}
          className="z-[110] w-auto max-w-[90vw] max-h-[90vh] rounded-sm"
          overlayClassName="z-[110]"
          headerProps={{ className: 'justify-center px-8 pt-10 pb-4' }}
          titleProps={{ className: 'pr-0 text-center text-xl' }}
          data-testid="disclaimer-dialog"
          onOpenAutoFocus={(event) => {
            // Lands focus on Accept so Enter or Space accepts, instead of the dialog title.
            event.preventDefault();
            acceptButtonRef.current?.focus();
          }}
        >
          <DialogBody className="max-h-[min(75vh,900px)] px-8 pt-0 pb-8">
            <p className="text-sm">
              {WELCOME_DISCLAIMER_INTRO.beforeDocuments}&nbsp;
              <DocumentLink document={TERMS_OF_USE_DOCUMENT} />
              &nbsp;{WELCOME_DISCLAIMER_INTRO.betweenDocuments}&nbsp;
              <DocumentLink document={PRIVACY_NOTICE_DOCUMENT} />
              {WELCOME_DISCLAIMER_INTRO.afterDocuments}
            </p>
            <ul className="my-4 list-outside list-disc px-8 text-sm space-y-2">
              {WELCOME_DISCLAIMER_BULLETS.map((segments) => (
                <li key={segments[0].text}>
                  <EmphasizedText segments={segments} />
                </li>
              ))}
              <li>
                <ConfidentialityNotice
                  leadIn={WELCOME_DISCLAIMER_LEAD_IN}
                  data-testid="disclaimer-confidentiality-notice"
                />
              </li>
            </ul>

            <div className="text-center mb-2">
              <Button
                ref={acceptButtonRef}
                id="accept-disclaimer"
                data-testid="disclaimer-accept-button"
                onClick={() => {
                  localStorage.setItem(DISCLAIMER_VIEWED_KEY, 'true');
                  rootStore.disclaimerViewed = true;
                }}
              >
                {WELCOME_DISCLAIMER_ACCEPT_LABEL}
              </Button>
            </div>
          </DialogBody>
        </DialogContent>
      </Dialog>
    );
  } else {
    return null;
  }
}
